import { Box, Grid, ResponsiveSection, Stack } from '@stitch-fix/mode-react';
import { JsonRteRenderer } from '../JsonRteRenderer';
import {
  CloudinaryImageWrapper,
  type CloudinaryImageFields,
} from '../CloudinaryImageWrapper';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
import { ModuleWrapper, type ModuleWrapperProps } from '../ModuleWrapper';
import type { ContentStackJSONRichText } from '../../utils/requests/contentstack/contentStackZodSchema';
import { MODE_MIN_WIDTH } from '../../utils/values/mediaQueries';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';

type GridItem = {
  content: {
    label?: ContentStackJSONRichText;
    text: ContentStackJSONRichText;
    placement: 'above' | 'below';
  };
  image: CloudinaryImageFields;
};

type DisplayGridBackgroundColor =
  | 'white'
  | 'gray-99'
  | 'mint-60'
  | 'blue-45'
  | 'forest-60'
  | 'grapefruit-60';

type DisplayGridTextColor =
  | 'blue-45'
  | 'mint-60'
  | 'white'
  | 'forest-60'
  | 'grapefruit-60'
  | 'citrus-60';

export type DisplayGridProps = {
  backgroundColor: DisplayGridBackgroundColor;
  items: GridItem[];
  // applies color to the content label, if provided; also serves as a fallback value for ModuleTitle and secondaryTextColor
  textColor?: DisplayGridTextColor;
  // applies color to the content text, if provided
  secondaryTextColor?: DisplayGridTextColor;
  majorTitleTextColor?: DisplayGridTextColor;
  minorTitleTextColor?: DisplayGridTextColor;
} & Omit<ModuleWrapperProps, 'children'>;

const resolveTextColor = (
  preferredColor: DisplayGridTextColor | undefined,
  backgroundColor: DisplayGridBackgroundColor,
) => {
  if (preferredColor === backgroundColor) {
    return backgroundColor === 'blue-45' ? 'mint-60' : 'blue-45';
  }

  return preferredColor;
};

export const DisplayGrid = ({
  alignment,
  backgroundColor = 'white',
  moduleTitle,
  moduleFooter,
  items,
  verticalSpacing,
  textColor,
  secondaryTextColor,
  majorTitleTextColor,
  minorTitleTextColor,
}: DisplayGridProps & ModularComponentProps) => {
  const color = resolveTextColor(textColor, backgroundColor);
  const secondaryColor = resolveTextColor(
    secondaryTextColor || color,
    backgroundColor,
  );
  const majorTitleColor = resolveTextColor(
    majorTitleTextColor,
    backgroundColor,
  );
  const minorTitleColor = resolveTextColor(
    minorTitleTextColor || majorTitleColor,
    backgroundColor,
  );

  return (
    <Box
      data-testid="display-grid-container"
      bgColor={backgroundColor}
      mt={verticalSpacing}
    >
      <ResponsiveSection width="xxlarge" px={{ sm: 1, lg: 3 }}>
        <ModuleWrapper
          alignment={alignment}
          moduleTitle={moduleTitle}
          moduleFooter={moduleFooter}
          includePadding={backgroundColor !== 'white'}
          textColor={color}
          majorTitleTextColor={majorTitleColor}
          minorTitleTextColor={minorTitleColor}
        >
          <Grid
            columns={{ sm: 1, md: 3 }}
            spacing={{ sm: [2.5, 1], lg: [2.5, 1.5] }}
            data-testid="grid"
          >
            {items.map((item, index) => {
              const jsonRteRenderer = !isEmpty(item.content.text?.children) ? (
                <Box
                  // List is static and won't change once rendered
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  <JsonRteRenderer
                    data={item.content.text}
                    options={{
                      color: secondaryColor,
                      textProps: {
                        p: {
                          spacingBottom: false,
                          setting: 'body-large',
                        },
                      },
                    }}
                  />
                </Box>
              ) : null;

              const itemLabel = !isEmpty(item.content.label?.children) ? (
                <Box
                  // List is static and won't change once rendered
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  px={0.5}
                >
                  <JsonRteRenderer
                    data={item.content.label}
                    options={{
                      color,
                      textSizeMap: 'display',
                      textProps: {
                        h3: {
                          spacingBottom: false,
                        },
                      },
                    }}
                  />
                </Box>
              ) : null;

              const image = item.image.src ? (
                <CloudinaryImageWrapper
                  // List is static and won't change once rendered
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  {...item.image}
                  sources={{
                    sm: 550,
                  }}
                  cloudinaryAsset={item.image.cloudinaryAsset}
                  sizes={[
                    { minWidth: 0, fluidWidth: 75 },
                    { minWidth: MODE_MIN_WIDTH.md, staticWidth: 384 },
                    { minWidth: MODE_MIN_WIDTH.xl, staticWidth: 496 },
                  ]}
                  imageProps={{ loading: 'lazy' }}
                />
              ) : null;

              return (
                // List is static and won't change once rendered
                // eslint-disable-next-line react/no-array-index-key
                <Stack spacing={0.5} key={index}>
                  {item.content.placement === 'above'
                    ? [itemLabel, jsonRteRenderer, image]
                    : [image, itemLabel, jsonRteRenderer]}
                </Stack>
              );
            })}
          </Grid>
        </ModuleWrapper>
      </ResponsiveSection>
    </Box>
  );
};
